import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { debugError } from '../../../utils/debugLog';

/**
 * ExecutionResultDisplay Component
 * Displays execution results with content check indicators
 */
const ExecutionResultDisplay = ({ executionResult, error }) => {
  const [copied, setCopied] = useState(false);

  if (error) {
    // Handle both string errors and execution result objects with error details
    const errorMessage =
      typeof error === 'string'
        ? error
        : error.message || error.error_message || 'Execution failed';
    const errorCode = error.error_code;
    const validationErrors =
      error.validation_errors || executionResult?.validation_errors;

    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Alert severity="error">
          <Typography variant="body2" fontWeight="bold">
            {errorCode ? `Execution Failed: ${errorCode}` : 'Execution Failed'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
          {validationErrors && validationErrors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                fontWeight="bold"
                display="block"
                gutterBottom
              >
                Validation Errors:
              </Typography>
              {validationErrors.map((err, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  component="div"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  • {err.message || err.field || JSON.stringify(err)}
                </Typography>
              ))}
            </Box>
          )}
        </Alert>
      </Paper>
    );
  }

  if (!executionResult) {
    return null;
  }

  const handleCopy = async text => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      debugError('Failed to copy:', err);
    }
  };

  const formatOutput = output => {
    if (!output) return '';

    // If it's a string, try to parse it as JSON first
    if (typeof output === 'string') {
      // Check if it looks like JSON (starts with { or [)
      if (output.trim().startsWith('{') || output.trim().startsWith('[')) {
        try {
          // Try parsing as JSON
          const parsed = JSON.parse(output);
          // If parsed successfully and has content field, extract it
          if (parsed && typeof parsed === 'object') {
            if (parsed.content && typeof parsed.content === 'string') {
              return parsed.content;
            }
            // If it's an array, get the last message's content
            if (Array.isArray(parsed) && parsed.length > 0) {
              const lastMessage = parsed[parsed.length - 1];
              if (lastMessage && lastMessage.content) {
                return lastMessage.content;
              }
            }
          }
        } catch (e) {
          // Not valid JSON, might be Python dict string - try to extract content
          // Look for 'content': "..." pattern (handles escaped quotes)
          // Pattern: 'content': " followed by content (handling escaped quotes) until closing "
          const contentMatch = output.match(/'content':\s*"((?:[^"\\]|\\.)*)"/);
          if (contentMatch && contentMatch[1]) {
            // Unescape the content
            return contentMatch[1]
              .replace(/\\n/g, '\n')
              .replace(/\\"/g, '"')
              .replace(/\\'/g, "'")
              .replace(/\\\\/g, '\\');
          }
          // Try single quotes pattern as fallback
          const contentMatchSingle = output.match(
            /'content':\s*'((?:[^'\\]|\\.)*)'/
          );
          if (contentMatchSingle && contentMatchSingle[1]) {
            return contentMatchSingle[1]
              .replace(/\\n/g, '\n')
              .replace(/\\'/g, "'")
              .replace(/\\\\/g, '\\');
          }
        }
      }
      // If it's a plain string, return as is
      return output;
    }

    // If it's an object, try to extract content from common structures
    if (typeof output === 'object') {
      // Handle message-like structure: {role: 'assistant', content: '...'}
      if (output.content && typeof output.content === 'string') {
        return output.content;
      }
      // Handle array of messages
      if (Array.isArray(output) && output.length > 0) {
        const lastMessage = output[output.length - 1];
        if (lastMessage && lastMessage.content) {
          return lastMessage.content;
        }
      }
      // Otherwise, stringify the object
      return JSON.stringify(output, null, 2);
    }
    return String(output);
  };

  // Priority: raw_response (LLM output) > output > structured_output > formatted_prompt (template with variables)
  const outputText =
    executionResult.raw_response ||
    executionResult.output ||
    executionResult.structured_output ||
    executionResult.formatted_prompt ||
    'No output available';

  const hasViolations = executionResult.has_violations === true;
  const contentCheckLogId = executionResult.content_check_log_id;
  const originalInput = executionResult.original_input;
  const anonymizedInput =
    executionResult.anonymized_input || executionResult.filtered_content;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6">Execution Results</Typography>
        </Box>
        <Tooltip title={copied ? 'Copied!' : 'Copy output'}>
          <IconButton
            size="small"
            onClick={() => handleCopy(formatOutput(outputText))}
          >
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content Check Indicators */}
      {hasViolations !== undefined && (
        <Box mb={2}>
          {hasViolations ? (
            <Alert severity="warning" icon={<SecurityIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Content Check: Violations Detected
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Sensitive content was detected and processed according to the
                assigned policy.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Content Check: No Violations
              </Typography>
            </Alert>
          )}

          {/* Original vs Anonymized Input Comparison */}
          {(originalInput || anonymizedInput) && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <InfoIcon fontSize="small" />
                  <Typography variant="body2">Content Check Details</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {originalInput && (
                    <Box mb={2}>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        display="block"
                        mb={0.5}
                      >
                        Original Input:
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          bgcolor: 'grey.50',
                          maxHeight: '200px',
                          overflow: 'auto',
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {typeof originalInput === 'object'
                            ? JSON.stringify(originalInput, null, 2)
                            : String(originalInput)}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {anonymizedInput && (
                    <Box>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        display="block"
                        mb={0.5}
                      >
                        Anonymized Input:
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          bgcolor: 'success.50',
                          maxHeight: '200px',
                          overflow: 'auto',
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {typeof anonymizedInput === 'object'
                            ? JSON.stringify(anonymizedInput, null, 2)
                            : String(anonymizedInput)}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {contentCheckLogId && (
                    <Box mt={2}>
                      <Link
                        href={`/content-check-audit/logs/${contentCheckLogId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                      >
                        View Audit Log
                      </Link>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Execution Output */}
      <Box>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Output:
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            maxHeight: '400px',
            overflow: 'auto',
          }}
        >
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {formatOutput(outputText)}
          </Typography>
        </Paper>
      </Box>

      {/* Metadata */}
      <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
        {executionResult.version_number && (
          <Chip
            label={`Version: ${executionResult.version_number}`}
            size="small"
            variant="outlined"
          />
        )}
        {executionResult.metadata?.model && (
          <Chip
            label={`Model: ${executionResult.metadata.model}`}
            size="small"
            variant="outlined"
          />
        )}
        {executionResult.created_at && (
          <Chip
            label={`Executed: ${new Date(executionResult.created_at).toLocaleString()}`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    </Paper>
  );
};

export default ExecutionResultDisplay;
